export type TenantInfo = {
  tenant_id: string;
  slug: string;
  name: string;
  role: string;
  color?: string;
  /** Desktop console toggle (add-desktop-console); owner/admin edits it. */
  desktop_enabled?: boolean;
};

export type AuthState = {
  tid: string | null;
  totp_set: boolean;
  role: string | null;
  is_superadmin?: boolean;
  name?: string | null;
  email?: string | null;
};

export type TotpEnrollResult = {
  secret: string;
  otpauth_uri: string;
  expires_in_seconds: number;
};

export type InviteAccepted = {
  tenant_id: string;
  role: string;
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Machine-readable discriminator from a structured `detail` (D4). */
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Fired with the fresh AuthState after a silent refresh. */
export const SESSION_REFRESHED_EVENT = "visera:session-refreshed";
/** Fired when the refresh itself fails: the session is over. */
export const SESSION_EXPIRED_EVENT = "visera:session-expired";

/** Single-flight silent refresh (change fix-app-shell-ux): concurrent
 * 401s share one rotation so refresh races never trip reuse detection. */
let refreshPromise: Promise<AuthState> | null = null;

async function silentRefresh(): Promise<AuthState> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const res = await fetch(apiUrl("/api/auth/refresh"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new ApiError(res.status, res.statusText);
      const text = await res.text();
      const state = (text ? JSON.parse(text) : null) as AuthState;
      saveSession(state);
      try {
        window.dispatchEvent(
          new CustomEvent<AuthState>(SESSION_REFRESHED_EVENT, { detail: state }),
        );
      } catch {
        // non-browser (tests): listeners are optional
      }
      return state;
    })();
    refreshPromise.catch(() => undefined).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function notifyExpired(): void {
  try {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  } catch {
    // non-browser (tests): listeners are optional
  }
}

/** Paths where a 401 is final (login/register/refresh itself). */
function isAuthFlow(path: string): boolean {
  return (
    path.endsWith("/auth/login") ||
    path.endsWith("/auth/refresh") ||
    path.endsWith("/auth/register") ||
    path.endsWith("/auth/verify-email") ||
    path.endsWith("/auth/resend-verification") ||
    path.endsWith("/auth/password/forgot") ||
    path.endsWith("/auth/password/reset")
  );
}

async function throwApiError(res: Response): Promise<never> {
  let detail: string | undefined;
  let code: string | undefined;
  try {
    const data = (await res.json()) as { detail?: unknown };
    const raw = data?.detail;
    if (typeof raw === "string") {
      detail = raw;
    } else if (raw !== undefined && raw !== null && typeof raw === "object") {
      // Structured detail (e.g. quota_exceeded or the login step codes):
      // keep it parseable and surface a clean message plus its code.
      const structured = raw as { code?: unknown; message?: unknown };
      if (typeof structured.code === "string") code = structured.code;
      detail =
        typeof structured.message === "string"
          ? structured.message
          : JSON.stringify(raw);
    }
  } catch {
    // non-JSON error body
  }
  throw new ApiError(res.status, detail ?? res.statusText, code);
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const doFetch = () =>
    fetch(apiUrl(path), {
      method: init?.method ?? "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...init,
    });
  let res = await doFetch();
  if (res.status === 401 && !isAuthFlow(path)) {
    // Sliding web session: one silent rotation, then a single retry.
    try {
      await silentRefresh();
    } catch {
      notifyExpired();
      await throwApiError(res);
    }
    res = await doFetch();
    if (!res.ok) {
      if (res.status === 401) notifyExpired();
      await throwApiError(res);
    }
  } else if (!res.ok) {
    await throwApiError(res);
  }
  // 204 (and other empty bodies) have nothing to parse.
  const text = await res.text();
  return (text ? (JSON.parse(text) as T) : (undefined as T)) as T;
}

/**
 * API origin prefix (change fix-app-shell-ux): `NEXT_PUBLIC_API_BASE` is
 * the origin where the API lives (e.g. https://visera.elcamilet.com in dev
 * outside the local proxy). Default "" = same-origin relative paths.
 * No-op when the path already starts with the base (dev "/api" value).
 */
const API_BASE = (process.env.NEXT_PUBLIC_API_BASE ?? "").replace(/\/$/, "");

export function apiUrl(path: string): string {
  if (!API_BASE || path.startsWith(`${API_BASE}/`)) return path;
  return `${API_BASE}${path}`;
}

const SESSION_KEY = "visera.session";

export function saveSession(session: AuthState): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // storage unavailable (private mode): non fatal
  }
}

export function readSession(): AuthState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as AuthState) : null;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}